<script setup lang="ts">

import TrialStatusContainer from "@/trial/components/trial-status/TrialStatusContainer.vue";
import Icon from "@/core/components/icon/Icon.vue";
import {Trial} from "@/core/interfaces/trial";
import {computed} from "vue";
import dateHelper from "../../../core/helpers/dateHelper";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {useRouter} from "vue-router";
import TrialService from "@/trial/services/TrialService";
import useHealthcenterAdvantages from "@/advantage/composables/useHealthcenterAdvantages";
import {Skeleton} from "@/core/components/ui/skeleton";
import MarkdownView from "@/core/components/markdown/Markdown.vue";
import {Calculator} from "@lucide/vue";
import AdvantagesAnimatedButton from "@/advantage/components/AdvantagesAnimatedButton.vue";
import GroupingAdvantageButton from "@/advantage/components/GroupingAdvantageButton.vue";

const router = useRouter();
const props = defineProps<{
    trial: Trial;
}>();
const {data, isLoading} = useHealthcenterAdvantages();

/**
 * Compute the remaining days of the trial
 */
const remainingDays = computed(() => {
    return TrialService.remainingDays(props.trial.end_at);
});

const navigateToGlobalPlan = () => {
    router.push({name: 'subscription-plans'});
};
</script>

<template>
  <div>
    <TrialStatusContainer>
      <template #headline>
        <div class="space-y-4">
          <div class="flex items-center gap-2">
            <div v-if="remainingDays <= 14" class="flex-1 font-medium">
              <p class="flex-1">
                Plus que <strong>{{ remainingDays }} {{ $filters.pluralize("jour", remainingDays) }}</strong> avant la
                fin
                de votre essai !
              </p>
              <p class="text-primary text-sm">
                Passez premium pour conserver vos avantages
              </p>
            </div>
            <p v-else class="flex-1 font-medium text-lg">
              Votre période d'essai se termine dans {{ remainingDays }}
              {{ $filters.pluralize("jour", remainingDays) }} (le {{
                dateHelper.shortFormat(trial.end_at, "DD MMMM")
              }})
            </p>
            <BaseButton primary @click="navigateToGlobalPlan">
              <Icon name="mdi-crown" class="mr-1" color="secondary" :size="30" />
              Passer premium
            </BaseButton>
          </div>
          <div>
            <Skeleton v-if="isLoading" class="w-48 h-4" />
            <div v-else class="space-y-2">
              <div v-for="advantage in data" :key="advantage.id" class="flex items-baseline">
                <p>🎁</p>
                <MarkdownView
                  :content="advantage.sales_pitch" class="text-primary font-bold text-lg"
                  :inherit-styles="true"
                />
              </div>
              <div class="flex flex-wrap gap-3">
                <BaseButton class="text-primary" :to="{name: 'extensions-profitability'}" density="comfortable">
                  <Calculator class="h-6 square mr-1 text-black" />
                  Combien me fait gagner Apodis ?
                </BaseButton>
                <GroupingAdvantageButton :healthcenter-has-advantages="!!data?.length" />
                <AdvantagesAnimatedButton />
              </div>
              <div class="flex text-primary text-sm items-start gap-2">
                <p class="flex-1">
                  À l'issue de votre période d'essai, vous passerez en Apodis Freemium
                </p>
                <router-link :to="{name: 'subscription-plans'}" class="underline">
                  En savoir plus
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </template>
    </TrialStatusContainer>
  </div>
</template>

<style scoped>

</style>
